<template>
    <span :id="`pagination-${id}`"
        class="pagination"
    >
        <div :id="`pagination__previous-${id}`"
            class="pagination__previous"
            @click="previousPage()"
        >
            <i class="icon-chevron" />
        </div>

        <div v-for="(page, index) in renderedPages"
            :id="`pagination__page-${id}`"
            :key="index"
            :class="[
                'pagination__page',
                {
                    'pagination__page--selected': isPageSelected(page),
                    'pagination__page--clickable': page,
                }
            ]"
            @click="setCurrentPage(page)"
        >
            {{ page || $t('global.pagination.seperator') }}
        </div>

        <div :id="`pagination__next-${id}`"
            class="pagination__next"
            @click="nextPage"
        >
            <i class="icon-chevron" />
        </div>
    </span>
</template>

<script>
export default {
    name: 'pagination',
    props: {
        // Id for automation
        id: {
            type: String,
            default: '',
        },
        page: {
            type: Number,
            default: 1,
        },
        lastPage: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            pages: [],
            currentPage: 0,
            timeout: null,
        };
    },
    computed: {
        totalPages() {
            return this.lastPage;
        },

        renderedPages() {
            const {
                totalPages,
                currentPage,
            } = this;

            return Array.from({
                length: this.totalPages,
            }, (_, i) => i + 1)
                .reduce((acc, page) => {
                    const previousItem = acc[acc.length - 1];

                    // No ellipsis
                    if (totalPages <= 5) {
                        return [...acc, page];
                    }

                    // Always render the first and last page
                    if (page === 1 || page === totalPages) {
                        return [...acc, page];
                    }

                    // Render the page if the current one is within [1,3]
                    if (page <= 3 && currentPage <= 3) {
                        return [...acc, page];
                    }

                    // Render the page if the current one is within [totalPage -3, totalPages]
                    if (page >= totalPages - 2 && currentPage >= totalPages - 2) {
                        return [...acc, page];
                    }

                    // Always render the current page
                    if (page === currentPage) {
                        return [...acc, page];
                    }

                    // Don't append if the previous item is an ellipsis
                    if (!previousItem) {
                        return acc;
                    }

                    return [...acc, false];
                }, []);
        },
    },
    watch: {
        page: {
            immediate: true,
            handler(value) {
                this.currentPage = value || 1;
            },
        },

        currentPage: {
            immediate: true,
            handler(value) {
                if (this.timeout) {
                    clearTimeout(this.timeout);
                }

                this.timeout = setTimeout(() => {
                    this.$emit('currentPage', value);
                }, 500);
            },
        },
    },
    methods: {
        isPageSelected(page) {
            return this.currentPage === page;
        },

        setCurrentPage(page) {
            this.currentPage = page || this.currentPage;
        },

        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            }
        },

        previousPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.pagination {
    display: flex;
    float: right;
    width: 100%;
    justify-content: center;

    @include min-width(md) {
        justify-content: flex-end;
    }

    &__previous {
        border-radius: 4px 0 0 4px;

        i {
            transform: rotate(-90deg);
        }
    }

    &__next {
        border-radius: 0 4px 4px 0;

        i {
            transform: rotate(90deg);
        }
    }

    &__previous,
    &__next,
    &__page {
        width: 3.6rem;
        height: 3.6rem;
        display: flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
    }

    &__previous,
    &__next {
        cursor: pointer;
        background: $white-color;
        border: 0.1rem solid $french-gray-color;

        i {
            font-size: 0.8rem;
        }

        &:hover {
            background: $athens-gray-color;
        }
    }

    &__page {
        font-size: 1.3rem;
        color: $cyprus-color;
        font-weight: 400;
        background: $white-color;
        border: 0.1rem solid $french-gray-color;

        &--clickable {
            cursor: pointer;

            &:hover {
                background: $athens-gray-color;
            }
        }

        &--selected {
            background: $athens-gray-color;
            font-weight: 700;
        }
    }
}
</style>